import { ReactElement } from "react";
import DropDownMultiple, {
  type DropDownOptionBase,
} from "../../../../components/staff/DropDownMultiple";
import type { FieldMap } from "../../../../interfaces/models/IIntegratorFirm";
import type { FeedMapField } from "./feedMapFieldCatalog";
import { isPathInsideCollection } from "./feedMapDictionary";

type EntityAttributeGridProps = {
  title: string;
  fields: FeedMapField[];
  mapping: {
    selectedPaths: FieldMap;
    options: DropDownOptionBase[];
    onPathChange: (fieldKey: string, feedPath: string | null) => void;
  };
  collectionPath: {
    options: DropDownOptionBase[];
    selectedPath: string;
    onPathChange: (feedPath: string | null) => void;
  };
};

/**
 * One entity's collection path, then its canonical fields as attribute | feed-path dropdown.
 */
export default function EntityAttributeGrid({
  title,
  fields,
  mapping,
  collectionPath,
}: EntityAttributeGridProps): ReactElement {
  return (
    <section className="min-w-0 py-6 sm:py-8 lg:px-4 lg:first:pl-0 lg:last:pr-0">
      <h2 className="mb-3 text-base font-semibold text-gray-font sm:mb-4 sm:text-xl">
        {title}
      </h2>
      <div className="flex w-full flex-col gap-3">
        <FeedPathRow
          label="Collection path"
          options={collectionPath.options}
          selectedPath={collectionPath.selectedPath}
          onPathChange={collectionPath.onPathChange}
        />
        <hr className="border-t border-gray-100" />
        {fields.map((field) => (
          <FeedPathRow
            key={field.key}
            label={field.label}
            options={mapping.options}
            selectedPath={mapping.selectedPaths[field.key]}
            insideCollection={collectionPath.selectedPath}
            onPathChange={(feedPath) => mapping.onPathChange(field.key, feedPath)}
          />
        ))}
      </div>
    </section>
  );
}

function FeedPathRow({
  label,
  options,
  selectedPath,
  insideCollection,
  onPathChange,
}: {
  label: string;
  options: DropDownOptionBase[];
  selectedPath: string | undefined;
  insideCollection?: string;
  onPathChange: (feedPath: string | null) => void;
}): ReactElement {
  const dropdownOptions =
    insideCollection === undefined
      ? optionsWithSelectedPath(options, selectedPath)
      : optionsInsideCollection(options, insideCollection, selectedPath);
  const value =
    dropdownOptions.find((option) => String(option.id) === selectedPath) ?? null;

  return (
    <div className="grid grid-cols-1 items-center gap-2 sm:grid-cols-[2fr_3fr] sm:gap-6">
      <span className="min-w-0 text-sm font-medium text-gray-font sm:text-base">
        {label}
      </span>
      <div className="min-w-0 w-full">
        <DropDownMultiple<DropDownOptionBase>
          options={dropdownOptions}
          value={value}
          onChange={(option) => onPathChange(option ? String(option.id) : null)}
          searchable
          renderInPortal
          emptyStateText={
            insideCollection !== undefined && !insideCollection.trim()
              ? "Select a collection path first"
              : insideCollection === undefined
                ? "Probe the feed to list nodes"
                : "Probe the feed to list fields"
          }
        />
      </div>
    </div>
  );
}

function optionsWithSelectedPath(
  options: DropDownOptionBase[],
  selectedPath: string | undefined
): DropDownOptionBase[] {
  if (!selectedPath) {
    return options;
  }
  if (options.some((option) => String(option.id) === selectedPath)) {
    return options;
  }
  return [{ id: selectedPath, name: selectedPath }, ...options];
}

function optionsInsideCollection(
  options: DropDownOptionBase[],
  collectionPath: string,
  selectedPath: string | undefined
): DropDownOptionBase[] {
  const collection = collectionPath.trim();
  if (!collection) {
    return [];
  }

  const children = options.filter((option) =>
    isPathInsideCollection(String(option.id), collection)
  );
  const withSelected =
    selectedPath &&
    isPathInsideCollection(selectedPath, collection) &&
    !children.some((option) => String(option.id) === selectedPath)
      ? [{ id: selectedPath, name: selectedPath }, ...children]
      : children;

  return withSelected.map((option) => ({
    ...option,
    name: pathWithoutPrefix(String(option.id), collection),
  }));
}

function pathWithoutPrefix(feedPath: string, collectionPath: string): string {
  return feedPath.slice(`${collectionPath}.`.length);
}
